import type {ReactNode} from 'react'
import {useEffect, useState} from 'react'
import type {LucideIcon} from 'lucide-react'
import {ArrowLeft} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Skeleton} from '@/components/ui/skeleton'
import type {WizardStep} from '@/components/shared/WizardStepper'
import {WizardStepper} from '@/components/shared/WizardStepper'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {useIsEmbedded} from '@/components/shared/EmbeddedContext'
import {usePageLayoutTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'

type PageLayoutVariant = 'list' | 'tabbed-list' | 'detail' | 'wizard' | 'dashboard'

export interface TabDefinition {
    key: string
    label: string
    count?: number
}

interface PageLayoutProps {
    variant: PageLayoutVariant
    title: string
    description?: ReactNode
    count?: number
    actions?: ReactNode
    icon?: LucideIcon
    backTo?: string | true
    onBack?: () => void
    stats?: ReactNode
    filters?: ReactNode
    pagination?: ReactNode
    emptyState?: ReactNode
    tabs?: TabDefinition[]
    activeTab?: string
    onTabChange?: (tab: string) => void
    wizardSteps?: WizardStep[]
    wizardCurrentStep?: number
    wizardNavigation?: ReactNode
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    embedded?: boolean
    'data-testid'?: string
    children: ReactNode
}

function PageLayoutContainer({
    embedded,
    testId,
    children,
}: {
    embedded?: boolean
    testId?: string
    children: ReactNode
}) {
    const testIdProps = testId !== undefined ? {'data-testid': testId} : {}

    return (
        <SectionContainer embedded={embedded} {...testIdProps}>
            {children}
        </SectionContainer>
    )
}

function BackButton({backTo, onBack}: {backTo: string | true; onBack?: () => void}) {
    const navigate = useNavigate()
    const i18n = useI18n()

    return (
        <Button
            data-testid="btn-back"
            variant="ghost"
            size="sm"
            onClick={() => {
                if (onBack) {
                    onBack()
                } else if (typeof backTo === 'string') {
                    navigate(backTo)
                } else {
                    navigate(-1)
                }
            }}
            className="mb-4"
        >
            <ArrowLeft className="h-4 w-4 mr-2"/>
            {i18n.common.back}
        </Button>
    )
}

function UniversalHeader({
    title,
    description,
    count,
    actions,
    icon: Icon,
}: {
    title: string
    description?: ReactNode
    count?: number
    actions?: ReactNode
    icon?: LucideIcon
}) {
    /* WHY: feed the central PageH1 (rendered once at the app shell) from the
       layout's title prop so every page that uses PageLayout automatically has
       a semantic <h1> with the right text. Issue 794 — Class A structural fix.
       Uses the high-priority slot so outer page-level usePageTitle calls (which
       often only carry the section-suffixed document.title text) can't
       overwrite the entity-specific layout title in the H1 context. */
    usePageLayoutTitle(title)
    return (
        <div className="mb-6" data-testid="page-layout-header">
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                    {Icon && (
                        <div
                            data-testid="page-layout-icon"
                            className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0"
                        >
                            <Icon className="h-6 w-6 text-primary"/>
                        </div>
                    )}
                    <div>
                        {/* WHY: the page's semantic <h1> is rendered once at the
                            app shell (PageTitleContext → PageH1). PageLayout's
                            visible heading is the same text styled as an H1 but
                            structurally an H2 so we don't ship two H1s. Issue
                            794 — Class A structural fix. */}
                        <h2
                            data-testid="page-layout-title"
                            className="text-2xl font-bold tracking-tight flex items-center"
                        >
                            {title}
                            {count !== undefined && (
                                <Badge
                                    data-testid="page-layout-count"
                                    variant="secondary"
                                    /* WHY: the count is decoration alongside the page title.
                                       Without aria-hidden the count digits get concatenated
                                       into the heading's accessible name
                                       ("All Identities238") so screen-reader users hear
                                       the count every time they land on the page. */
                                    aria-hidden="true"
                                    className="ml-2 text-xs font-normal bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/20 hover:text-foreground tabular-nums"
                                >
                                    {count}
                                </Badge>
                            )}
                        </h2>
                        {description !== undefined && (
                            <div
                                data-testid="page-layout-description"
                                className="text-sm text-muted-foreground mt-1"
                            >
                                {description}
                            </div>
                        )}
                    </div>
                </div>
                {actions !== undefined && (
                    <div data-testid="page-layout-actions" className="flex gap-2 items-center">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    )
}

function ListSkeleton() {
    return (
        <div data-testid="loading-skeleton" className="space-y-3 p-4">
            {Array.from({length: 5}, (_, i) => (
                <Skeleton key={i} className="h-10 w-full"/>
            ))}
        </div>
    )
}

function DetailSkeleton() {
    return (
        <div data-testid="loading-skeleton" className="space-y-6">
            <Card>
                <CardContent className="pt-6 space-y-4">
                    {Array.from({length: 4}, (_, i) => (
                        <div key={i} className="grid grid-cols-2 gap-4">
                            <Skeleton className="h-5 w-24"/>
                            <Skeleton className="h-5 w-48"/>
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    )
}

function WizardSkeleton() {
    return (
        <div data-testid="loading-skeleton" className="space-y-6">
            <div className="flex justify-center gap-4">
                {Array.from({length: 3}, (_, i) => (
                    <Skeleton key={i} className="h-8 w-8 rounded-full"/>
                ))}
            </div>
            <Card>
                <CardContent className="pt-6">
                    <Skeleton className="h-32 w-full"/>
                </CardContent>
            </Card>
        </div>
    )
}

function DashboardSkeleton() {
    return (
        <div data-testid="loading-skeleton" className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
                {Array.from({length: 3}, (_, i) => (
                    <Skeleton key={i} className="h-24 w-full"/>
                ))}
            </div>
            <Skeleton className="h-48 w-full"/>
        </div>
    )
}

function ListVariant({
    filters,
    pagination,
    emptyState,
    count,
    loading,
    error,
    onRetry,
    children,
}: {
    filters?: ReactNode
    pagination?: ReactNode
    emptyState?: ReactNode
    count?: number
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    children: ReactNode
}) {
    const showEmpty = count === 0

    const renderContent = () => {
        if (loading) {
            return <ListSkeleton/>
        }
        if (error) {
            const errorObj = error instanceof Error ? error : new Error(String(error))
            return <ErrorBanner error={errorObj} onRetry={onRetry}/>
        }
        if (showEmpty && emptyState) {
            return emptyState
        }
        return (
            <>
                {children}
                {!showEmpty && pagination}
            </>
        )
    }

    return (
        <Card data-testid="page-layout-card">
            <CardContent className="pt-6">
                {filters && !loading && !error && (
                    <div data-testid="page-layout-filters" className="mb-4 flex gap-2 items-center flex-wrap">
                        {filters}
                    </div>
                )}
                {renderContent()}
            </CardContent>
        </Card>
    )
}

function TabbedListVariant({
    tabs,
    activeTab: controlledActiveTab,
    onTabChange,
    loading,
    error,
    onRetry,
    children,
}: {
    tabs: TabDefinition[]
    activeTab?: string
    onTabChange?: (tab: string) => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    children: ReactNode
}) {
    const [internalActiveTab, setInternalActiveTab] = useState(tabs[0]?.key ?? '')
    const activeTab = controlledActiveTab ?? internalActiveTab
    const setActiveTab = (tab: string) => {
        setInternalActiveTab(tab)
        onTabChange?.(tab)
    }

    if (loading) {
        return (
            <div>
                <div className="mb-4">
                    <Skeleton className="h-10 w-64" data-testid="loading-skeleton"/>
                </div>
                <Card data-testid="page-layout-card">
                    <CardContent className="pt-6">
                        <ListSkeleton/>
                    </CardContent>
                </Card>
            </div>
        )
    }

    if (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error))
        return (
            <Card data-testid="page-layout-card">
                <CardContent className="pt-6">
                    <ErrorBanner error={errorObj} onRetry={onRetry}/>
                </CardContent>
            </Card>
        )
    }

    return (
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="page-layout-tabs">
            <TabsList data-testid="page-layout-tab-bar">
                {tabs.map(tab => (
                    <TabsTrigger key={tab.key} value={tab.key} data-testid={`tab-${tab.key}`}>
                        {tab.label}
                        {tab.count !== undefined && (
                            <Badge
                                variant="secondary"
                                className="ml-2 text-xs font-normal bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/20 hover:text-foreground tabular-nums"
                            >
                                {tab.count}
                            </Badge>
                        )}
                    </TabsTrigger>
                ))}
            </TabsList>
            {tabs.map(tab => (
                <TabsContent key={tab.key} value={tab.key}>
                    <Card data-testid="page-layout-card">
                        <CardContent className="pt-6">
                            {tab.key === activeTab ? children : null}
                        </CardContent>
                    </Card>
                </TabsContent>
            ))}
        </Tabs>
    )
}

function DetailVariant({
    loading,
    error,
    onRetry,
    children,
}: {
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    children: ReactNode
}) {
    if (loading) {
        return <DetailSkeleton/>
    }
    if (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error))
        return <ErrorBanner error={errorObj} onRetry={onRetry}/>
    }
    return (
        <div className="grid gap-6" data-testid="page-layout-content">
            {children}
        </div>
    )
}

function WizardVariant({
    wizardSteps,
    wizardCurrentStep,
    wizardNavigation,
    loading,
    error,
    onRetry,
    children,
}: {
    wizardSteps?: WizardStep[]
    wizardCurrentStep?: number
    wizardNavigation?: ReactNode
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    children: ReactNode
}) {
    if (loading) {
        return <WizardSkeleton/>
    }
    if (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error))
        return <ErrorBanner error={errorObj} onRetry={onRetry}/>
    }
    return (
        <>
            {wizardSteps && wizardCurrentStep !== undefined && (
                <WizardStepper
                    steps={wizardSteps}
                    currentStep={wizardCurrentStep}
                />
            )}
            <Card data-testid="page-layout-card">
                <CardContent className="pt-6">
                    {children}
                </CardContent>
            </Card>
            {wizardNavigation && (
                <div data-testid="page-layout-wizard-nav" className="flex justify-end gap-2 mt-6">
                    {wizardNavigation}
                </div>
            )}
        </>
    )
}

function DashboardVariant({
    loading,
    error,
    onRetry,
    children,
}: {
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    children: ReactNode
}) {
    if (loading) {
        return <DashboardSkeleton/>
    }
    if (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error))
        return <ErrorBanner error={errorObj} onRetry={onRetry}/>
    }
    return (
        <div data-testid="page-layout-content" className="space-y-6">
            {children}
        </div>
    )
}

export interface TabPanelContentProps {
    title?: string
    titleTestId?: string
    description?: ReactNode
    count?: number
    filters?: ReactNode
    pagination?: ReactNode
    children: ReactNode
    'data-testid'?: string
}

export function TabPanelContent({
    title,
    titleTestId,
    description,
    count,
    filters,
    pagination,
    children,
    'data-testid': testId,
}: TabPanelContentProps) {
    return (
        <div data-testid={testId ?? 'tab-panel-content'}>
            {(title !== undefined || filters !== undefined) && (
                <div className="flex items-center justify-between flex-wrap gap-4 mb-4" data-testid="tab-panel-header">
                    {title !== undefined && (
                        <div>
                            <h2
                                data-testid={titleTestId ?? 'tab-panel-title'}
                                className="text-lg font-semibold leading-none tracking-tight flex items-center"
                            >
                                {title}
                                {count !== undefined && (
                                    <Badge
                                        data-testid="tab-panel-count"
                                        variant="secondary"
                                        className="ml-2 text-xs font-normal bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/20 hover:text-foreground tabular-nums"
                                    >
                                        {count}
                                    </Badge>
                                )}
                            </h2>
                            {description !== undefined && (
                                <div
                                    data-testid="tab-panel-description"
                                    className="text-sm text-muted-foreground mt-1"
                                >
                                    {description}
                                </div>
                            )}
                        </div>
                    )}
                    {filters !== undefined && (
                        <div
                            data-testid="tab-panel-filters"
                            className={title === undefined
                                ? 'flex gap-2 items-center flex-wrap w-full'
                                : 'flex gap-2 items-center flex-wrap'
                            }
                        >
                            {filters}
                        </div>
                    )}
                </div>
            )}
            {children}
            {pagination !== undefined && (
                <div data-testid="tab-panel-pagination">
                    {pagination}
                </div>
            )}
        </div>
    )
}

export function PageLayout({
    variant,
    title,
    description,
    count,
    actions,
    icon,
    backTo,
    onBack,
    stats,
    filters,
    pagination,
    emptyState,
    tabs,
    activeTab,
    onTabChange,
    wizardSteps,
    wizardCurrentStep,
    wizardNavigation,
    loading,
    error,
    onRetry,
    embedded,
    'data-testid': testId,
    children,
}: PageLayoutProps) {
    const contextEmbedded = useIsEmbedded()
    const isEmbedded = embedded ?? contextEmbedded

    useEffect(() => {
        document.title = `${title}\u00A0\u2014 IAM` // eslint-disable-line i18next/no-literal-string -- document.title with app name suffix
    }, [title])

    const showBackButton = backTo !== undefined && (variant === 'detail' || variant === 'wizard')
    const resolvedChildren = loading || error ? null : children
    const variantSupportsStats = variant === 'list' || variant === 'tabbed-list' || variant === 'dashboard'
    const showStats = stats !== undefined && variantSupportsStats && !loading && !error

    return (
        <PageLayoutContainer embedded={isEmbedded} testId={testId}>
            {showBackButton && <BackButton backTo={backTo} onBack={onBack}/>}
            <UniversalHeader
                title={title}
                description={description}
                count={count}
                actions={actions}
                icon={icon}
            />
            {showStats && (
                <div data-testid="page-layout-stats" className="mb-6">
                    {stats}
                </div>
            )}
            {variant === 'list' && (
                <ListVariant
                    filters={filters}
                    pagination={pagination}
                    emptyState={emptyState}
                    count={count}
                    loading={loading}
                    error={error}
                    onRetry={onRetry}
                >
                    {resolvedChildren}
                </ListVariant>
            )}
            {variant === 'tabbed-list' && tabs && (
                <TabbedListVariant
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    loading={loading}
                    error={error}
                    onRetry={onRetry}
                >
                    {resolvedChildren}
                </TabbedListVariant>
            )}
            {variant === 'detail' && (
                <DetailVariant loading={loading} error={error} onRetry={onRetry}>
                    {resolvedChildren}
                </DetailVariant>
            )}
            {variant === 'wizard' && (
                <WizardVariant
                    wizardSteps={wizardSteps}
                    wizardCurrentStep={wizardCurrentStep}
                    wizardNavigation={wizardNavigation}
                    loading={loading}
                    error={error}
                    onRetry={onRetry}
                >
                    {resolvedChildren}
                </WizardVariant>
            )}
            {variant === 'dashboard' && (
                <DashboardVariant loading={loading} error={error} onRetry={onRetry}>
                    {resolvedChildren}
                </DashboardVariant>
            )}
        </PageLayoutContainer>
    )
}
